import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { type JSX } from "react";
import { Typography } from "@platform/design-system";

import { RateScheduleFormData } from "../../types/models";

/** Check if coverage type is voluntary life (dependent rates always separate) */
const isVoluntaryLife = (coverageType?: string): boolean =>
  coverageType?.toLowerCase() === "voluntarylifeadnd";

interface AdndRateConfigSectionProps {
  formData: RateScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RateScheduleFormData>>;
  coverageType?: string;
}

/**
 * AD&D rate configuration UI, extracted to reduce parent component complexity.
 * Shown for all flat rate designs.
 */
export function AdndRateConfigSection({
  formData,
  setFormData,
  coverageType,
}: AdndRateConfigSectionProps): JSX.Element {
  const isVolLife = isVoluntaryLife(coverageType);

  const handleAdndRatesApplyChange = (value: boolean): void => {
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      adndRatesApply: value,
      // Reset dependent fields when toggling off
      ...(!value && {
        dependentAdndRatesApply: false,
        dependentAdndType: undefined,
      }),
    }));
  };

  const handleDependentAdndRatesApplyChange = (value: boolean): void => {
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      dependentAdndRatesApply: value,
      // For Vol Life, always separate; for Basic Life, reset to let user choose
      dependentAdndType: value
        ? isVolLife
          ? "separate"
          : prev.dependentAdndType
        : undefined,
    }));
  };

  const handleDependentAdndTypeChange = (
    value: "combined" | "separate",
  ): void => {
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      dependentAdndType: value,
    }));
  };

  return (
    <Box sx={{ border: 1, borderColor: "divider", p: 2, borderRadius: 1 }}>
      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
        AD&D Rates
      </Typography>

      {/* Is employee AD&D included? */}
      <FormControl
        component="fieldset"
        sx={{ mb: 2, display: "flex", flexDirection: "column" }}
      >
        <FormLabel component="legend">Is employee AD&D included?</FormLabel>
        <RadioGroup
          value={formData.adndRatesApply ? "yes" : "no"}
          onChange={(e) => handleAdndRatesApplyChange(e.target.value === "yes")}
        >
          <FormControlLabel
            value="yes"
            control={<Radio color="brandPrimary" />}
            label="Yes"
          />
          <FormControlLabel
            value="no"
            control={<Radio color="brandPrimary" />}
            label="No"
          />
        </RadioGroup>
      </FormControl>

      {formData.adndRatesApply && (
        <>
          {/* Is there a Dependent AD&D rate? */}
          <FormControl
            component="fieldset"
            sx={{ mb: 2, display: "flex", flexDirection: "column" }}
          >
            <FormLabel component="legend">
              Is there a Dependent AD&D rate?
            </FormLabel>
            <RadioGroup
              value={formData.dependentAdndRatesApply ? "yes" : "no"}
              onChange={(e) =>
                handleDependentAdndRatesApplyChange(e.target.value === "yes")
              }
            >
              <FormControlLabel
                value="yes"
                control={<Radio color="brandPrimary" />}
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio color="brandPrimary" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>

          {formData.dependentAdndRatesApply && (
            <>
              {/* Combined or Separate (Basic Life only; Vol Life is always separate) */}
              {!isVolLife && (
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">
                    Dependent AD&D rates combined or separate?
                  </FormLabel>
                  <RadioGroup
                    value={formData.dependentAdndType || "combined"}
                    onChange={(e) =>
                      handleDependentAdndTypeChange(
                        e.target.value as "combined" | "separate",
                      )
                    }
                  >
                    <FormControlLabel
                      value="combined"
                      control={<Radio color="brandPrimary" />}
                      label="Combined"
                    />
                    <FormControlLabel
                      value="separate"
                      control={<Radio color="brandPrimary" />}
                      label="Separate"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
